import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { UserModel } from "app/models/user.model";
import { AuthenticationService } from "app/services/authentication.service";
import { BrowserNotificationService } from "app/services/browserNotification.service";
import { ProjectService } from "app/services/api/project.service";
import { UserService } from "app/services/api/user.service";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { locale as catalan } from "../i18n/ca";
import { locale as english } from "../i18n/en";
import { locale as spanish } from "../i18n/es";

@Component({
    selector: "app-browser-notification-dialog",
    templateUrl: "./browser-notification-dialog.component.html",
    styleUrls: ["./browser-notification-dialog.component.scss"],
    standalone: false,
})
export class BrowserNotificationDialogComponent implements OnInit {
    viewState:
        | "ready"
        | "enabled"
        | "permissionDenied"
        | "notConfigured"
        | "unsupported"
        | "error" = "ready";
    currentUser: UserModel | null = null;
    loadError = "";
    updating = false;

    constructor(
        private readonly userService: UserService,
        private readonly projectService: ProjectService,
        private readonly authenticationService: AuthenticationService,
        private readonly browserNotificationService: BrowserNotificationService,
        private readonly translation: FuseTranslationLoaderService,
        private readonly toastr: ToastrService,
        private readonly dialogRef: MatDialogRef<BrowserNotificationDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        readonly data: {
            publicKey: string;
            projectId?: string;
            projectName?: string;
        },
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
    }

    ngOnInit(): void {
        this.currentUser = this.authenticationService.currentUser;

        if (!this.data.publicKey) {
            this.viewState = "notConfigured";
            return;
        }

        if (!this.browserNotificationService.isSupported()) {
            this.viewState = "unsupported";
            return;
        }

        const permissionState = this.browserNotificationService.getPermissionState();

        if (permissionState === "denied") {
            this.viewState = "permissionDenied";
            return;
        }

        this.viewState = this.browserNotificationsEnabled && permissionState === "granted"
            ? "enabled"
            : "ready";
    }

    get browserNotificationsEnabled(): boolean {
        return this.currentUser?.browserNotificationsEnabled === true;
    }

    get browserNotificationsEnabledForThisBrowser(): boolean {
        return (
            this.browserNotificationsEnabled &&
            this.browserNotificationService.hasGrantedPermission()
        );
    }

    get hasProjectContext(): boolean {
        return Boolean(this.data.projectId);
    }

    get browserNotificationsDisabledForProject(): boolean {
        if (!this.data.projectId) {
            return false;
        }

        return (
            this.currentUser?.browserNotificationDisabledProjectIds?.includes(
                this.data.projectId,
            ) ?? false
        );
    }

    get stateMessage(): string {
        switch (this.viewState) {
            case "enabled":
                return this.translation.translateService.instant(
                    "TOOLBAR.BrowserNotificationsEnabledBody",
                );
            case "permissionDenied":
                return this.translation.translateService.instant(
                    "TOOLBAR.BrowserNotificationsPermissionDenied",
                );
            case "notConfigured":
                return this.translation.translateService.instant(
                    "TOOLBAR.BrowserNotificationsNotConfigured",
                );
            case "unsupported":
                return this.translation.translateService.instant(
                    "TOOLBAR.BrowserNotificationsUnsupported",
                );
            case "error":
                return this.loadError;
            default:
                return this.translation.translateService.instant(
                    "TOOLBAR.BrowserNotificationsPromptBody",
                );
        }
    }

    get projectStateMessage(): string {
        return this.translation.translateService.instant(
            this.browserNotificationsDisabledForProject
                ? "TOOLBAR.BrowserNotificationsProjectStatusMuted"
                : "TOOLBAR.BrowserNotificationsProjectStatusEnabled",
        );
    }

    async enableNotifications() {
        if (this.updating || this.browserNotificationsEnabledForThisBrowser) {
            return;
        }

        this.updating = true;
        try {
            const subscription = await this.browserNotificationService.subscribe(
                this.data.publicKey,
            );
            subscription.language = this.getSubscriptionLanguage();
            const updatedUser = await firstValueFrom(
                this.userService.subscribeBrowserNotifications(subscription),
            );
            this.currentUser = updatedUser;
            this.authenticationService.setCurrentUser(updatedUser);
            this.viewState = "enabled";
            this.toastr.success(
                this.translation.translateService.instant(
                    "TOOLBAR.BrowserNotificationsEnabled",
                ),
            );
            this.dialogRef.close(true);
        } catch (error) {
            this.loadError =
                error instanceof Error
                    ? error.message
                    : this.translation.translateService.instant(
                          "TOOLBAR.BrowserNotificationsError",
                      );
            this.viewState = "error";
            this.toastr.error(this.loadError);
        } finally {
            this.updating = false;
        }
    }

    async toggleProjectNotifications(enabled: boolean) {
        if (
            this.updating ||
            !this.browserNotificationsEnabledForThisBrowser ||
            !this.data.projectId
        ) {
            return;
        }

        this.updating = true;
        try {
            const updatedUser = await firstValueFrom(
                enabled
                    ? this.projectService.enableBrowserNotifications(this.data.projectId)
                    : this.projectService.disableBrowserNotifications(this.data.projectId),
            );

            this.currentUser = updatedUser;
            this.authenticationService.setCurrentUser(updatedUser);
            this.toastr.success(
                this.translation.translateService.instant(
                    enabled
                        ? "TOOLBAR.BrowserNotificationsProjectEnabled"
                        : "TOOLBAR.BrowserNotificationsProjectMuted",
                    { projectName: this.data.projectName || "" },
                ),
            );
        } catch (error) {
            this.toastr.error(
                error instanceof Error
                    ? error.message
                    : this.translation.translateService.instant(
                          "TOOLBAR.BrowserNotificationsError",
                      ),
            );
        } finally {
            this.updating = false;
        }
    }

    close() {
        this.dialogRef.close(false);
    }

    private getSubscriptionLanguage(): string {
        const currentLanguage =
            this.translation.translateService.currentLang ||
            this.translation.translateService.getDefaultLang();

        if (currentLanguage?.trim()) {
            return currentLanguage.trim().toLowerCase();
        }

        if (typeof navigator !== "undefined" && navigator.language?.trim()) {
            return navigator.language.trim().toLowerCase();
        }

        return "en";
    }
}
